import React, { FC } from "react";
import { useTranslation } from "react-i18next";
import { Card, Typography, Space, Button, Tag } from "antd";
import { GiftOutlined, ThunderboltOutlined, CheckCircleOutlined } from "@ant-design/icons";
import { VoucherStatus } from "../helpers/api";
import { displayCurrency } from "../helpers/functions";

const { Text } = Typography;

interface VoucherBalanceCardProps {
  onOpenVoucherDeposit: () => void;
  voucher: VoucherStatus | null;
}

export const VoucherBalanceCard: FC<VoucherBalanceCardProps> = ({
  onOpenVoucherDeposit,
  voucher,
}) => {
  const { t, i18n } = useTranslation();

  // Статус ещё не приехал, либо ваучер истёк / выключен — не показываем ничего,
  // чтобы не обещать того, чего сервер не начислит.
  if (!voucher || voucher.status === "expired" || voucher.status === "disabled") {
    return null;
  }

  const used = voucher.status === "used";

  // Дату срока действия форматируем на языке интерфейса, а не жёстко по-русски.
  const validUntilLabel = (() => {
    const d = new Date(voucher.valid_until.replace(" ", "T"));
    return isNaN(d.getTime())
      ? voucher.valid_until
      : d.toLocaleDateString(i18n.language || undefined, { day: "numeric", month: "long" });
  })();

  return (
    <Card
      style={{
        background: "#fff",
        borderRadius: "16px",
        border: used ? "1px solid #f0f0f0" : "1px solid #d9f7be",
        boxShadow: "0 2px 8px rgba(82, 196, 26, 0.05)",
        marginBottom: "12px",
      }}
      bodyStyle={{ padding: "14px 16px" }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Space size={12} align="center">
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 12,
              background: used ? "#fafafa" : "#f6ffed",
              border: `1px solid ${used ? "#d9d9d9" : "#b7eb8f"}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: used ? "#8c8c8c" : "#52c41a",
              fontSize: 20,
            }}
          >
            {used ? <CheckCircleOutlined /> : <GiftOutlined />}
          </div>
          <div>
            <Space size={6} align="center">
              <Text strong style={{ fontSize: "14px", lineHeight: "1.2" }}>
                {t("Multi-Voucher Doubling")}
              </Text>
              <Tag
                color={used ? "default" : "green"}
                style={{ borderRadius: "8px", fontWeight: 700, margin: 0, fontSize: "10px" }}
              >
                {used ? t("USED") : "2X"}
              </Tag>
            </Space>
            <Text type="secondary" style={{ fontSize: "12px", display: "block", marginTop: 2 }}>
              {used && voucher.used
                ? t("Credited +{{amount}} {{currency}}", {
                    amount: voucher.used.bonus_amount,
                    currency: displayCurrency(voucher.used.currency),
                  })
                : t("Doubles your top-up (GRAM / USDT) • Until {{date}}", {
                    date: validUntilLabel,
                  })}
            </Text>
          </div>
        </Space>

        {!used && (
          <Button
            type="primary"
            size="middle"
            icon={<ThunderboltOutlined />}
            onClick={onOpenVoucherDeposit}
            style={{
              borderRadius: "10px",
              fontWeight: 600,
              background: "#52c41a",
              borderColor: "#52c41a",
              boxShadow: "0 2px 6px rgba(82, 196, 26, 0.2)",
            }}
          >
            {t("Double")}
          </Button>
        )}
      </div>
    </Card>
  );
};

export default VoucherBalanceCard;
